import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Alert,
  Spin,
  Progress,
  Tag,
  Timeline
} from 'antd';
import {
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ScanOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import apiClient from '../utils/apiClient';
import moment from 'moment';

const { Title, Text } = Typography;

function PaymentStatus() {
  const [paymentStats, setPaymentStats] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  useEffect(() => {
    fetchPaymentData();
    // Auto refresh every 30 seconds
    const interval = setInterval(fetchPaymentData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPaymentData = async () => {
    setLoading(true);
    try {
      // Fetch payment stats
      const statsResponse = await apiClient.get('/api/payment-scanner/payment-stats?days=1');
      setPaymentStats(statsResponse.data);

      // Fetch recent transactions
      const transactionsResponse = await apiClient.get('/api/payment-scanner/processed-transactions?limit=5');
      setRecentTransactions(transactionsResponse.data.transactions || []);

      setLastCheck(new Date());
    } catch (error) {
      console.error('Error fetching payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return moment(dateString).format('DD/MM/YYYY HH:mm:ss');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processed': return 'green';
      case 'failed': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'processed': return 'Processed';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  };

  return (
    <div>
      <div className="page-header">
        <Title level={2}>
          <Space>
            <ScanOutlined />
            <span>Payment Status</span>
          </Space>
        </Title>
        <Text type="secondary">Monitor the automatic payment scanning system</Text>
      </div>

      {/* System Status */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={12}>
            <Space>
              <ScanOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
              <div>
                <Title level={4} style={{ margin: 0 }}>Payment Scanning System</Title>
                <Text type="secondary">Running normally</Text>
              </div>
            </Space>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Space>
              <Text type="secondary">
                Last updated: {lastCheck ? formatDate(lastCheck.toISOString()) : 'Never'}
              </Text>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={fetchPaymentData}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Statistics */}
      {paymentStats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Today's Transactions"
                value={paymentStats.total_transactions}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Total Amount"
                value={paymentStats.total_amount}
                formatter={(value) => formatPrice(value)}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Users"
                value={paymentStats.unique_users}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Average"
                value={paymentStats.average_amount}
                formatter={(value) => formatPrice(value)}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Recent Transactions */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card 
            title="Recent Transactions"
            extra={
              <Button
                type="link"
                onClick={() => window.location.href = '/payment-history'}
              >
                View All
              </Button>
            }
          >
            <Spin spinning={loading}>
              {recentTransactions.length > 0 ? (
                <Timeline>
                  {recentTransactions.map((transaction, index) => (
                    <Timeline.Item
                      key={transaction.id}
                      color={getStatusColor(transaction.status)}
                      dot={
                        transaction.status === 'processed' ? 
                        <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                        <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                      }
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong>{formatPrice(transaction.amount)}</Text>
                          <br />
                          <Text type="secondary" style={{ fontFamily: 'monospace' }}>
                            {transaction.content}
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {formatDate(transaction.processed_at)}
                          </Text>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <Tag color={getStatusColor(transaction.status)}>
                            {getStatusText(transaction.status)}
                          </Tag>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            ID: {transaction.transaction_id}
                          </Text>
                        </div>
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <ClockCircleOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                  <br />
                  <Text type="secondary">No transactions yet</Text>
                </div>
              )}
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* System Information */}
      <Card style={{ marginTop: 24 }}>
        <Title level={4}>System Information</Title>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Alert
              message="How It Works"
              description="The system automatically scans bank transactions every 5 minutes and processes payments based on the transfer content."
              type="info"
              showIcon
            />
          </Col>
          
        </Row>
      </Card>
    </div>
  );
}

export default PaymentStatus;